import { QBankStructureLevelCategory } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Table, TableContainer, type TableContainerProps } from '@shared/components';
import { useMemo } from 'react';

import { QBankStructureDashBoardTableHeader } from '../../components';
import {
  type Tag,
  type LearningObjectiveStatement,
  type ItemSetQuestionPropertiesData,
} from '../../types';
import { getShallowestEnabledNonCourseLevel } from '../../utils';
import { QBankStructureItemSetQuestionDashboardTableBody } from '../qbank-structure-item-set-question-dashboard-table-body';

import { formatDataToQBankStructureLosItemSetQuestionDashboardTable } from './utils';

interface QBankStructureItemSetQuestionDashboardTableProps extends TableContainerProps {
  data: ItemSetQuestionPropertiesData[];
  difficultyTags: Tag[];
  calculationTags: Tag[];
  los: LearningObjectiveStatement[];
}
export default function QBankStructureLosItemSetQuestionDashboardTable(
  props: Readonly<QBankStructureItemSetQuestionDashboardTableProps>,
) {
  const { data, difficultyTags, calculationTags, los, ...restProps } = props;
  const { qBankStructureLevelItems, qBankStructureLevels } = useQBankStructure();

  const defaultLevel = getShallowestEnabledNonCourseLevel(qBankStructureLevels);

  const qBankStructureLevelItemsToUse = useMemo(
    () =>
      qBankStructureLevelItems.filter((item) =>
        [
          QBankStructureLevelCategory.Course,
          QBankStructureLevelCategory.Part,
          QBankStructureLevelCategory.Group,
          QBankStructureLevelCategory.Chapter,
          QBankStructureLevelCategory.Subject,
        ].includes(item.qBankStructureLevel.category),
      ),
    [qBankStructureLevelItems],
  );

  const formattedData = useMemo(
    () =>
      formatDataToQBankStructureLosItemSetQuestionDashboardTable({
        qBankStructureLevelItems: qBankStructureLevelItemsToUse,
        los,
        itemSetQuestionPropertiesData: data,
        difficultyTags,
        calculationTags,
      }),
    [qBankStructureLevelItemsToUse, los, data, difficultyTags, calculationTags],
  );

  if (!defaultLevel) {
    return null;
  }

  return (
    <TableContainer
      {...restProps}
      sx={{
        overflow: 'hidden',
        ...restProps.sx,
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <QBankStructureDashBoardTableHeader />
        <QBankStructureItemSetQuestionDashboardTableBody
          rows={formattedData}
          defaultLevel={defaultLevel.category}
        />
      </Table>
    </TableContainer>
  );
}
